import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { getThemeById } from '../config/themes'
import type { TenantThemeConfig } from '../types/theme'
import {
  getBirthdayDisplayPromotionConfig,
  getFacebookShareDisplayPromotionConfig,
  type UserManualReviewBonusDisplaySettings,
} from '../services/promotionConfigService'
import { resolveLineContactHref, resolveLineContactId } from '../utils/contactInfo'
import './ModernContactPage.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernContactPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

type ContactTopic = 'birthday-bonus' | 'facebook-share-bonus'

const TOPIC_HELP_CONTENT: Record<
  ContactTopic,
  {
    promotionType: 'birthday_display' | 'facebook_share_display'
    title: string
    subtitle: string
    sectionTitle: string
    sectionSubtitle: string
    requirements: string[]
  }
> = {
  'birthday-bonus': {
    promotionType: 'birthday_display',
    title: 'ขอรับโบนัสวันเกิด',
    subtitle: 'ติดต่อแอดมินพร้อมส่งหลักฐานเพื่อยืนยันวันเกิดก่อนอนุมัติรับสิทธิ์',
    sectionTitle: 'เอกสารที่ต้องส่งให้แอดมิน',
    sectionSubtitle: 'ส่งข้อมูลให้ครบถ้วนก่อนทัก LINE เพื่อให้แอดมินตรวจสอบและอนุมัติได้รวดเร็วขึ้น',
    requirements: [
      'ส่งรูปบัตรประชาชนที่เห็นชื่อและวันเดือนปีเกิดชัดเจน',
      'แจ้ง username หรือเบอร์โทรที่ใช้สมัครสมาชิก',
      'รอแอดมินตรวจสอบความถูกต้องก่อนรับโบนัส',
    ],
  },
  'facebook-share-bonus': {
    promotionType: 'facebook_share_display',
    title: 'ขอรับโบนัสแชร์เฟส',
    subtitle: 'ติดต่อแอดมินพร้อมส่งหลักฐานการแชร์โพสต์เพื่อให้ทีมงานตรวจสอบก่อนอนุมัติ',
    sectionTitle: 'หลักฐานที่ต้องส่งให้แอดมิน',
    sectionSubtitle: 'เตรียมภาพและลิงก์ให้ครบก่อนติดต่อ เพื่อให้ทีมงานตรวจสอบได้ทันที',
    requirements: [
      'แคปภาพโพสต์ Facebook ที่แชร์แล้วให้เห็นชัดเจน',
      'ส่ง URL ของโพสต์ที่แชร์เพื่อให้แอดมินเปิดตรวจสอบได้',
      'แจ้ง username หรือเบอร์โทรที่ใช้สมัครสมาชิก',
    ],
  },
}

function normalizeTopicHelpSettings(
  source: Partial<UserManualReviewBonusDisplaySettings> | undefined,
  fallback: { title: string; subtitle: string; sectionTitle: string; sectionSubtitle: string; requirements: string[] }
): { title: string; subtitle: string; sectionTitle: string; sectionSubtitle: string; requirements: string[] } {
  const requirements =
    Array.isArray(source?.requirements) && source.requirements.length > 0
      ? source.requirements.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : fallback.requirements

  return {
    title: typeof source?.pageTitle === 'string' && source.pageTitle.trim() ? source.pageTitle : fallback.title,
    subtitle:
      typeof source?.pageSubtitle === 'string' && source.pageSubtitle.trim() ? source.pageSubtitle : fallback.subtitle,
    sectionTitle:
      typeof source?.contactSectionTitle === 'string' && source.contactSectionTitle.trim()
        ? source.contactSectionTitle
        : fallback.sectionTitle,
    sectionSubtitle:
      typeof source?.contactSectionSubtitle === 'string' && source.contactSectionSubtitle.trim()
        ? source.contactSectionSubtitle
        : fallback.sectionSubtitle,
    requirements,
  }
}

const IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

/** หน้าติดต่อเรา — โทน HTD + LINE CTA */
const ModernContactPage: React.FC<ModernContactPageProps> = ({ themeConfig, assetConfig }) => {
  const [searchParams] = useSearchParams()
  const [copied, setCopied] = useState(false)
  const [topicHelpOverride, setTopicHelpOverride] = useState<{
    title: string
    subtitle: string
    sectionTitle: string
    sectionSubtitle: string
    requirements: string[]
  } | null>(null)

  const themeFolder = useMemo(
    () => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue',
    [themeConfig.themeId]
  )

  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }

  const bgUrl = useMemo(() => getAssetUrl('/img/bg.jpg'), [assetConfig.CDN_BASE_URL, themeFolder])

  const contactInfo = themeConfig.settings.contactInfo
  const lineId = resolveLineContactId(contactInfo)
  const lineHref = resolveLineContactHref(contactInfo) ?? `https://line.me/ti/p/${lineId}`
  const phone = String(contactInfo?.phone || '').trim()

  const topic = searchParams.get('topic') as ContactTopic | null
  const topicHelpBase = topic && topic in TOPIC_HELP_CONTENT ? TOPIC_HELP_CONTENT[topic] : null

  useEffect(() => {
    document.body.classList.add('htd-contact-active')
    return () => document.body.classList.remove('htd-contact-active')
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!topicHelpBase) {
      setTopicHelpOverride(null)
      return
    }

    const loadTopicContent = async () => {
      try {
        const response =
          topicHelpBase.promotionType === 'birthday_display'
            ? await getBirthdayDisplayPromotionConfig(themeConfig.tenantId)
            : await getFacebookShareDisplayPromotionConfig(themeConfig.tenantId)
        if (cancelled) return
        setTopicHelpOverride(normalizeTopicHelpSettings(response?.data?.settings, topicHelpBase))
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load topic help config', error)
          setTopicHelpOverride(normalizeTopicHelpSettings(undefined, topicHelpBase))
        }
      }
    }

    void loadTopicContent()
    return () => {
      cancelled = true
    }
  }, [themeConfig.tenantId, topicHelpBase])

  const topicHelp = topicHelpBase ? topicHelpOverride || normalizeTopicHelpSettings(undefined, topicHelpBase) : null

  const handleCopyLineId = async () => {
    try {
      await navigator.clipboard.writeText(lineId)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div
      className="htd-contact"
      style={{ ['--htd-contact-bg' as string]: `url(${bgUrl})` }}
    >
      <div className="htd-contact__ambient" aria-hidden>
        <span className="htd-contact__orb htd-contact__orb--a" />
        <span className="htd-contact__orb htd-contact__orb--b" />
      </div>

      <div className="htd-contact__shell">
        <header className="htd-contact__head">
          <p className="htd-contact__eyebrow">Support 24 hrs</p>
          <h1 className="htd-contact__title">ติดต่อเรา</h1>
          <p className="htd-contact__lead">
            มีพนักงานรอต้อนรับลูกค้าตลอด 24 ชั่วโมง ช่วยเหลือ ตอบคำถาม และแก้ไขปัญหาได้ทันที
          </p>
        </header>

        {topicHelp ? (
          <section className="htd-contact__topic" aria-label={topicHelp.title}>
            <div className="htd-contact__topic-top">
              <div>
                <p className="htd-contact__topic-kicker">{topicHelp.sectionTitle}</p>
                <h2 className="htd-contact__topic-title">{topicHelp.title}</h2>
                <p className="htd-contact__topic-sub">{topicHelp.subtitle}</p>
              </div>
              <div className="htd-contact__topic-badge">
                <span>Bonus Guide</span>
                <strong>{topic === 'birthday-bonus' ? 'Birthday Bonus' : 'Facebook Share Bonus'}</strong>
              </div>
            </div>
            <p className="htd-contact__topic-hint">{topicHelp.sectionSubtitle}</p>
            <div className="htd-contact__steps">
              {topicHelp.requirements.map((requirement, index) => (
                <article key={requirement} className="htd-contact__step">
                  <span className="htd-contact__step-num">Step {index + 1}</span>
                  <p>{requirement}</p>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <section className="htd-contact__card" aria-label="ช่องทางติดต่อ LINE">
          <a
            href={lineHref}
            target="_blank"
            rel="noopener noreferrer"
            className="htd-contact__line-icon"
            aria-label={`เปิดแชท LINE ${lineId}`}
          >
            <img
              src={getAssetUrl('/img/LINE_logo.svg.png')}
              alt=""
              onError={(e) => {
                const el = e.currentTarget
                if (el.dataset.fallback === '1') {
                  el.src = IMG_PLACEHOLDER
                  return
                }
                el.dataset.fallback = '1'
                el.src = getAssetUrl('/img/line_icon.png')
              }}
            />
            <span className="htd-contact__line-pulse" aria-hidden />
          </a>

          <p className="htd-contact__hint-top">หรือกดที่ไอคอน LINE ได้เลย</p>

          <div className="htd-contact__line-id-row">
            <a href={lineHref} target="_blank" rel="noopener noreferrer" className="htd-contact__line-id">
              LINE ID : {lineId}
            </a>
            <button type="button" className="htd-contact__copy" onClick={() => void handleCopyLineId()}>
              <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden />
              {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
            </button>
          </div>

          <p className="htd-contact__at-note">(มี @ ด้านหน้า)</p>

          {phone ? (
            <a href={`tel:${phone.replace(/\D/g, '')}`} className="htd-contact__phone">
              <i className="fas fa-phone-alt" aria-hidden />
              {phone}
            </a>
          ) : null}

          <ul className="htd-contact__perks">
            <li>
              <i className="fas fa-clock" aria-hidden />
              บริการตลอด 24 ชั่วโมง
            </li>
            <li>
              <i className="fas fa-headset" aria-hidden />
              ช่วยเหลือ ตอบคำถาม แก้ไขปัญหา
            </li>
            <li>
              <i className="fas fa-user-shield" aria-hidden />
              ติดต่อแอดมินได้โดยตรงผ่าน LINE
            </li>
          </ul>

          <a href={lineHref} target="_blank" rel="noopener noreferrer" className="htd-contact__cta">
            <i className="fab fa-line" aria-hidden />
            แชทกับแอดมินทันที
          </a>
        </section>

        <div className="htd-contact__foot">
          <Link to="/" className="htd-contact__back">
            <i className="fas fa-chevron-left" aria-hidden />
            กลับหน้าแรก
          </Link>
        </div>
      </div>
    </div>
  )
}

export default ModernContactPage
